import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where, orderBy } from 'firebase/firestore';
import { app } from 'services/firebase';
import { HistoryEntry } from 'types';
import { logger } from 'utils/secureLogger';
import { loadUserFromRef } from 'services/firebaseApi';

interface UseDocHistoryProps {
    storageId: string;
    categoryId?: string;
    locationId?: string;
    customerId?: string;
    supplierId?: string;
    productId?: string;
    transactionId?: string;
    actionId?: string;
}

export interface DocHistoryData {
    historyEntries: HistoryEntry[];
    docType: string;
}

const useDocHistory = ({
    storageId,
    categoryId,
    locationId,
    customerId,
    supplierId,
    productId,
    transactionId,
    actionId
}: UseDocHistoryProps) => {
    const getDocType = () => {
        if (actionId && productId) return 'action';
        if (transactionId) return 'transaction';
        if (productId) return 'product';
        if (supplierId) return 'supplier';
        if (customerId) return 'customer';
        if (categoryId) return 'category';
        if (locationId) return 'location';
        return 'storage';
    };

    const docType = getDocType();
    
    const getQueryParams = () => {
        if (actionId && productId) {
            return { docType: 'action', ids: { actionId, productId } };
        }
        if (transactionId) {
            return { docType: 'transaction', ids: { transactionId } };
        }
        if (productId) {
            return { docType: 'product', ids: { productId } };
        }
        if (supplierId) {
            return { docType: 'supplier', ids: { supplierId } };
        }
        if (customerId) {
            return { docType: 'customer', ids: { customerId } };
        }
        if (categoryId) {
            return { docType: 'category', ids: { categoryId } };
        }
        if (locationId) {
            return { docType: 'location', ids: { locationId } };
        }
        return { docType: 'storage', ids: {} };
    };

    const queryParams = getQueryParams();

    const {
        data: historyData,
        isLoading,
        error,
        refetch
    } = useQuery<DocHistoryData>({
        queryKey: ['docHistory', storageId, queryParams.docType, queryParams.ids],
        queryFn: async () => {
            try {
                logger.info('Fetching document history from historyEntries collection', { 
                    storageId, 
                    ...queryParams 
                }, 'useDocHistory');
                
                const db = getFirestore(app);
                const historyCollectionRef = collection(db, `storages/${storageId}/historyEntries`);
                
                // Build query to filter by docType
                let historyQuery = query(
                    historyCollectionRef,
                    where('docType', '==', queryParams.docType)
                );

                // Add filters based on ids
                const { ids } = queryParams;
                if (queryParams.docType === 'action' && ids.actionId && ids.productId) {
                    historyQuery = query(
                        historyCollectionRef,
                        where('docType', '==', queryParams.docType),
                        where('ids.actionId', '==', ids.actionId),
                        where('ids.productId', '==', ids.productId)
                    );
                } else if (queryParams.docType === 'product' && ids.productId) {
                    historyQuery = query(
                        historyCollectionRef,
                        where('docType', '==', queryParams.docType),
                        where('ids.productId', '==', ids.productId)
                    );
                } else if (queryParams.docType === 'category' && ids.categoryId) {
                    historyQuery = query(
                        historyCollectionRef,
                        where('docType', '==', queryParams.docType),
                        where('ids.categoryId', '==', ids.categoryId)
                    );
                } else if (queryParams.docType === 'customer' && ids.customerId) {
                    historyQuery = query(
                        historyCollectionRef,
                        where('docType', '==', queryParams.docType),
                        where('ids.customerId', '==', ids.customerId)
                    );
                } else if (queryParams.docType === 'supplier' && ids.supplierId) {
                    historyQuery = query(
                        historyCollectionRef,
                        where('docType', '==', queryParams.docType),
                        where('ids.supplierId', '==', ids.supplierId)
                    );
                } else if (queryParams.docType === 'transaction' && ids.transactionId) {
                    historyQuery = query(
                        historyCollectionRef,
                        where('docType', '==', queryParams.docType),
                        where('ids.transactionId', '==', ids.transactionId)
                    );
                }

                // Add ordering by timestamp descending (newest first)
                historyQuery = query(historyQuery, orderBy('timestamp', 'desc'));

                const querySnapshot = await getDocs(historyQuery);
                
                // Load history entries and their associated user data
                const historyEntries: HistoryEntry[] = await Promise.all(
                    querySnapshot.docs.map(async (doc) => {
                        const data = doc.data();
                        
                        // Load user data from userRef if it exists
                        let user;
                        if (data.userRef) {
                            user = await loadUserFromRef(data.userRef);
                        }
                        
                        return {
                            type: data.type,
                            userId: data.userId || user?.id || '',
                            userRef: data.userRef,
                            user,
                            timestamp: data.timestamp || null,
                            ids: data.ids || {},
                            docType: data.docType,
                        } as HistoryEntry;
                    })
                );

                logger.info('Document history fetched successfully', { 
                    storageId,
                    ...queryParams,
                    entriesCount: historyEntries.length 
                }, 'useDocHistory');

                return {
                    historyEntries,
                    docType: queryParams.docType
                };
            } catch (error) {
                logger.error('Failed to fetch document history', error, 'useDocHistory');
                throw error;
            }
        },
        enabled: !!storageId
    });

    return {
        historyEntries: historyData?.historyEntries || [],
        docType: historyData?.docType || docType,
        isLoading,
        error,
        refetch
    };
};

export default useDocHistory;
